import React from "react";
import { MessageCircle } from "lucide-react";
import { Message } from "../../../types/conversations";
import { formatTime } from "../../../helpers/conversations";
import conversationsService from "../../../services/conversations";
import { useAuth } from "../../../context/AuthContext";
import { WizardStepId } from "./types";
import { LinkThreeData } from "../../../types";
import { WizardImageUpload } from "./components/WizardImageUpload";

interface WizardMessage extends Message {
  wizardType?: "question" | "confirmation" | "error" | "component" | "info";
  component?: React.ReactNode;
  actions?: Array<{
    label: string;
    onClick: () => void;
    variant?: "primary" | "secondary";
  }>;
}

interface WizardMessageItemProps {
  message: WizardMessage;
  showDate: boolean;
  currentStep: WizardStepId;
  onImageUpload: (photoBase64: string | null) => void;
  profileData: LinkThreeData | null;
}

export const WizardMessageItem: React.FC<WizardMessageItemProps> = ({
  message,
  showDate,
  currentStep,
  onImageUpload,
  profileData,
}) => {
  const { getUser } = useAuth();
  const authUser = getUser();
  const currentUserIdFromService = conversationsService.getCurrentUserId();
  const currentUserId = authUser?.id || currentUserIdFromService;

  const currentUserIdNum =
    currentUserId !== null && currentUserId !== undefined
      ? Number(currentUserId)
      : null;
  const messageSenderIdNum = Number(message.senderId);

  const isFromMe =
    currentUserIdNum !== null &&
    !Number.isNaN(currentUserIdNum) &&
    !Number.isNaN(messageSenderIdNum) &&
    messageSenderIdNum === currentUserIdNum;

  // Se é do bot (senderId === 0)
  const isFromBot = message.senderId === 0;

  return (
    <React.Fragment>
      {showDate && (
        <div className="flex justify-center my-3 md:my-4">
          <span className="bg-white dark:bg-gray-800 px-2.5 py-1 rounded-lg text-[11px] text-gray-600 dark:text-gray-400 shadow-sm md:px-3 md:text-xs">
            {(() => {
              const date =
                message.timestamp instanceof Date
                  ? message.timestamp
                  : new Date(message.timestamp);

              if (Number.isNaN(date.getTime())) {
                return "Data inválida";
              }

              return date.toLocaleDateString("pt-BR", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              });
            })()}
          </span>
        </div>
      )}

      <div
        className={`flex ${isFromMe ? "justify-end" : "justify-start"} mb-3`}
      >
        {/* Avatar do bot */}
        {isFromBot && (
          <div className="flex-shrink-0 mr-2 md:mr-3">
            <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center">
              <MessageCircle className="w-4 h-4 text-white" />
            </div>
          </div>
        )}

        <div
          className={`max-w-[85%] md:max-w-[70%] ${isFromMe ? "order-2" : "order-1"}`}
        >
          {/* Mensagem principal */}
          <div
            className={`rounded-2xl px-4 py-2.5 shadow-sm ${
              isFromMe
                ? "bg-purple-500 text-white rounded-br-none"
                : "bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-tl-none"
            }`}
          >
            {/* Ícone para tipos especiais */}
            {message.wizardType === "confirmation" && (
              <div className="flex items-center gap-2 mb-2">
                <span className="text-green-500 text-sm">
                  ✅ Salvo com sucesso!
                </span>
              </div>
            )}
            {message.wizardType === "error" && (
              <div className="flex items-center gap-2 mb-2">
                <span className="text-red-500 text-sm">❌ Erro ao salvar.</span>
              </div>
            )}

            {/* Conteúdo da mensagem */}
            <p className="text-[13px] whitespace-pre-wrap break-words md:text-sm">
              {message.content || "(sem conteúdo)"}
            </p>

            {/* Horário */}
            <div
              className={`flex items-center gap-1 mt-1.5 ${
                isFromMe ? "justify-end" : "justify-start"
              }`}
            >
              <span
                className={`text-[10px] ${
                  isFromMe
                    ? "text-white/70"
                    : "text-gray-500 dark:text-gray-400"
                }`}
              >
                {formatTime(message.timestamp)}
              </span>
            </div>
          </div>

          {/* Componente embutido */}
          {message.component && <div className="mt-3">{message.component}</div>}

          {/* Componente de upload de imagem */}
          {message.wizardType === "component" &&
            currentStep === "profile_photo" &&
            !message.component && (
              <div className="mt-3">
                <WizardImageUpload
                  onImageSelect={(base64) => onImageUpload(base64)}
                  onSkip={() => onImageUpload(null)}
                  currentImage={profileData?.photo}
                />
              </div>
            )}

          {/* Botões de ação */}
          {message.actions && message.actions.length > 0 && (
            <div className="mt-3 flex flex-col gap-2 md:flex-row md:gap-3">
              {message.actions.map((action) => (
                <button
                  key={action.label}
                  onClick={action.onClick}
                  className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 active:scale-95 shadow-sm ${
                    action.variant === "primary"
                      ? "bg-purple-600 hover:bg-purple-700 text-white"
                      : "bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
                  }`}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};
